if (!customElements.get('formhous-machine-options')) {
  customElements.define('formhous-machine-options', class extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.setupSwatches();
      // Move Dawn's original quantity control; keep its IDs, rules and event handlers.
      const quantity = this.productInfo?.querySelector('.product-form__quantity');
      const slot = this.closest('form')?.querySelector('[data-fh-quantity-slot]');
      if (quantity && slot && !slot.contains(quantity)) slot.append(quantity);
      this.logoGroup = this.querySelector('[data-fh-logo]');
      this.textGroup = this.querySelector('[data-fh-text]');
      this.file = this.querySelector('input[type="file"]');
      this.artworkFiles ||= [];
      this.text = this.querySelector('textarea');
      this.onChange = (event) => {
        if (event.target.name === 'id' || event.target.closest('variant-selects')) this.syncBranding();
        if (event.target === this.file) {
          this.artworkFiles.push(...Array.from(this.file.files));
          this.file.value = '';
          this.renderArtwork();
          this.validateFile();
          this.file.reportValidity();
        }
      };
      this.onInput = () => { this.querySelector('[data-fh-count]').textContent = this.text.value.length; };
      this.productInfo?.addEventListener('change', this.onChange);
      this.text.addEventListener('input', this.onInput);
      this.syncBranding();
    }
    disconnectedCallback() {
      this.productInfo?.removeEventListener('change', this.onChange);
      this.text?.removeEventListener('input', this.onInput);
    }
    setupSwatches() {
      // Exact display values from build.formhous.com/palette.js (September 12, 2026).
      const colors = {
        A:'#252525', B:'#f1ecdf', C:'#8a9192', D:'#e7d7bf', E:'#704124', F:'#bdd99b',
        G:'#192d50', H:'#ee7917', I:'#91d1e1', J:'#36a7a5', K:'#9266bb', L:'#f1ce34',
        M:'#dfa0ae', N:'#d91d23', O:'#cd4853', P:'#145dc0', Q:'#5a7360',
        1:'#202224', 2:'#f3f3ef', 3:'#a4a5a5', 4:'#5d6063', 5:'#dec4a0', 6:'#994126',
        7:'#f07b18', 8:'#efc52b', 9:'#148e62', 10:'#df2028', 11:'#1254ca'
      };
      for (const select of this.querySelectorAll('select[name="properties[Primary Color]"], select[name="properties[Accent Color]"]')) {
        if (select.swatchUI) continue;
        const ui = document.createElement('div');
        ui.className = 'fh-swatches';
        const label = select.previousElementSibling;
        label.id = select.id + '-label';
        ui.setAttribute('role', 'group');
        ui.setAttribute('aria-labelledby', label.id);
        const chosen = document.createElement('p');
        chosen.className = 'fh-swatch-selected';
        chosen.setAttribute('aria-live', 'polite');
        const error = document.createElement('p');
        error.id = select.id + '-error';
        error.className = 'fh-swatch-error';
        error.setAttribute('role', 'alert');
        error.hidden = true;
        for (const group of select.querySelectorAll('optgroup')) {
          const section = document.createElement('div');
          const title = document.createElement('p');
          const pla = /^[A-Q] -/.test(group.querySelector('option').value);
          title.className = 'fh-swatch-material';
          title.textContent = pla ? 'PLA · 17 colors' : 'PETG · 11 colors · slight sheen';
          const grid = document.createElement('div');
          grid.className = 'fh-swatch-grid';
          for (const option of group.querySelectorAll('option')) {
            const code = option.value.split(' - ')[0];
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'fh-swatch';
            button.dataset.value = option.value;
            button.setAttribute('aria-label', option.value + (pla ? ' · PLA' : ' · PETG'));
            button.setAttribute('aria-describedby', error.id);
            button.title = button.getAttribute('aria-label');
            const chip = document.createElement('span');
            chip.className = 'fh-swatch-chip';
            chip.style.backgroundColor = colors[code];
            chip.setAttribute('aria-hidden', 'true');
            const caption = document.createElement('span');
            caption.textContent = code;
            button.append(chip, caption);
            button.addEventListener('click', () => {
              select.value = option.value;
              select.dispatchEvent(new Event('change', {bubbles:true}));
            });
            grid.append(button);
          }
          section.append(title, grid);
          ui.append(section);
        }
        ui.append(chosen, error);
        select.after(ui);
        select.swatchUI = ui;
        select.swatchError = error;
        const sync = () => {
          for (const button of ui.querySelectorAll('button')) button.setAttribute('aria-pressed', String(button.dataset.value === select.value));
          chosen.textContent = select.value || 'Choose a color above';
          if (select.value) { error.hidden = true; ui.removeAttribute('aria-invalid'); }
        };
        select.addEventListener('change', sync);
        sync();
        // Keep the original named select as the single source for Shopify's order properties.
        select.hidden = true;
        label.removeAttribute('for');
      }
    }
    syncBranding() {
      let variant;
      try {
        variant = JSON.parse(this.productInfo?.querySelector('[data-selected-variant]')?.textContent || 'null');
      } catch (_) { variant = null; }
      const branding = variant?.options?.[Number(this.dataset.brandingIndex)] || '';
      const logo = /uv printed (logo|sign)|full branding setup/i.test(branding);
      const text = /3d text sign/i.test(branding);
      // Unknown branding stays visible rather than silently dropping customer input.
      const known = /^(no logo|no branding|no sign|3d text sign|uv printed logo|uv printed sign|full branding setup)$/i.test(branding);
      this.logoGroup.hidden = known && !logo;
      this.textGroup.hidden = known && !text;
      this.file.disabled = this.logoGroup.hidden;
      this.text.disabled = this.textGroup.hidden;
      this.validateFile();
    }
    getArtworkFiles() {
      return [...this.artworkFiles, ...Array.from(this.file.files)];
    }
    renderArtwork() {
      const list = this.querySelector('[data-fh-artwork-list]');
      list.replaceChildren();
      this.artworkFiles.forEach((file, index) => {
        const item = document.createElement('li');
        const name = document.createElement('span');
        name.textContent = `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.textContent = 'Remove';
        remove.setAttribute('aria-label', `Remove ${file.name}`);
        remove.addEventListener('click', () => {
          this.artworkFiles.splice(index, 1);
          this.renderArtwork();
          this.validateFile();
          (list.querySelectorAll('button')[Math.min(index, this.artworkFiles.length - 1)] || this.file).focus();
        });
        item.append(name, remove);
        list.append(item);
      });
      this.querySelector('[data-fh-artwork-status]').textContent = this.artworkFiles.length ? `${this.artworkFiles.length} file(s) selected. Files upload when you add the machine to your cart.` : 'No files selected.';
    }
    appendArtwork(formData) {
      // Unique property keys prevent Shopify treating multiple files as one value.
      formData.delete('properties[Logo]');
      if (this.file.disabled) return;
      this.getArtworkFiles().forEach((file, index) => {
        formData.append(index === 0 ? 'properties[Logo]' : `properties[Logo ${index + 1}]`, file, file.name);
      });
    }
    validateFile() {
      let message = '';
      if (!this.file.disabled) {
        for (const file of this.getArtworkFiles()) {
          if (!/\.(png|jpe?g|webp|svg|pdf)$/i.test(file.name)) message = `${file.name}: choose a PNG, JPG, WEBP, SVG, or PDF file.`;
          else if (file.size > 10 * 1024 * 1024) message = `${file.name}: please choose a file no larger than 10 MB.`;
          if (message) break;
        }
      }
      this.file.setCustomValidity(message);
      return !message;
    }
    validate() {
      this.syncBranding();
      this.validateFile();
      for (const control of this.querySelectorAll('select,input,textarea')) {
        if (control.swatchUI && !control.checkValidity()) {
          control.swatchError.textContent = control.name.includes('Primary') ? 'Please choose a primary color.' : 'Please choose an accent color.';
          control.swatchError.hidden = false;
          control.swatchUI.setAttribute('aria-invalid', 'true');
          control.swatchUI.querySelector('button').focus();
          return false;
        }
        if (!control.disabled && !control.reportValidity()) return false;
      }
      return true;
    }
  });
}
